import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  BotaoSelecao,
  Checkbox,
  ETAPAS_COTACAO,
  Field,
  FormActions,
  FormSection,
  Input,
  PageHeader,
  Select,
  Stepper,
} from '../../../ui';

import ModalMarcas from '../../../components/modal/marcas/ModalMarcas';
import ModalVeiculos from '../../../components/modal/veiculos/ModalVeiculos';
import ModalTipoVeiculos from '../../../components/modal/tipo-veiculo/ModalTipoVeiculos';
import ModalCotas from '../../../components/modal/cotas/ModalCotas';
import ModalRegionaisUsuario from '../../../components/modal/regionais-usuario/ModalRegionaisUsuario';
import ModalCooperativasUsuario from '../../../components/modal/cooperativas-usuario/ModalCooperativasUsuario';

import { criarCotacao, editarCotacao, calcularCotaParticipacao } from '../../../api/cotacao/cotacaoApi';
import { calcularRateio } from '../../../api/rateio/rateioApi';
import { Cota } from '../../../api/sga-cota/sga-cotaApi';
import { TipoVeiculo } from '../../../api/sga-tipo-veiculo/sga-tipo-veiculoApi';

import { setCotacaoData } from '../../../store/cotacaoSlice';
import { RootState, store } from '../../../store/store';

import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatPlaca, formatTelefone, placaValida } from '../../../utils/mascaras';
import { anosDisponiveis } from '../../../utils/anosVeiculo';
import { numeroParaMoeda, parseMoeda } from '../../../utils/moeda';
import {
  validarCpfCnpj,
  validarEmail,
  validarObrigatorio,
} from '../../../utils/validacaoAssociado';

import { COTACAO_PRODUTO } from '../../cotacao-produto/routes';
import { HOME } from '../../home/routes';

/**
 * `/cotacao` — **etapa 1 do fluxo de cotação** (reescrita na at-62, frente D/D1).
 *
 * O consultor descreve quem está cotando e o veículo, escolhe o perfil
 * (tipo, regional, cooperativa, cota), e a tela cria o **lead**
 * (`AssociadoCotacao`) e leva à seleção de produtos, onde o PDF é gerado.
 *
 * ## O que a reescrita mudou de COMPORTAMENTO
 *
 *   1. **Fim dos 3 segundos** de espera antes de navegar. O consultor está de pé
 *      na frente do cliente, e a spec 03 §4 é explícita: *"não é requisito — é um
 *      defeito de experiência"*. O toast sobrevive à navegação (container único).
 *   2. **Erro junto do campo**, com `role="alert"`. Antes: o botão ficava
 *      desabilitado enquanto faltasse nome/placa/marca/modelo/ano — **sem dizer
 *      qual** — e os outros cinco obrigatórios (tipo, regional, cooperativa,
 *      cota, FIPE) só apareciam no submit, num toast genérico.
 *   3. **Validação de verdade**: CPF com dígito verificador, e-mail e celular no
 *      formato que o backend exige. Antes um e-mail malformado voltava como
 *      **400** depois de o consultor ter preenchido a tela toda.
 *   4. 🔴 **CPF passou a ser OBRIGATÓRIO aqui** (P1 do plano). Ele era opcional
 *      nesta tela e **exigido na seguinte** — `GerarCotacaoPdfDto.cpfAssociado` é
 *      `@IsNotEmpty` e o service localiza o lead **pelo CPF**. Lead sem CPF era um
 *      beco sem saída: o consultor escolhia os produtos e recebia
 *      *"cpfAssociado should not be empty"*.
 *   5. **A tela abre PREENCHIDA** quando já há uma cotação no fluxo — é o que faz
 *      o "Voltar" da etapa 2 ser útil. Antes ela nascia vazia **mas a validação
 *      lia regional e cooperativa do slice**: a tela dizia "Nenhuma regional
 *      selecionada" e o envio passava assim mesmo.
 *   6. 🔴 **Reenviar EDITA o lead em vez de criar outro** (P2 do plano) — ver
 *      {@link enviar}. Sem isso, o "Voltar" novo faria cada correção de e-mail
 *      consumir uma das **4 cotações do mês** daquele CPF.
 *   7. **Stepper** "Etapa 1 de 2" e **"Voltar"** no cabeçalho.
 *   8. **A cota é zerada** quando muda algo de que ela depende (modelo, tipo,
 *      regional, cooperativa) — ela é escolhida pela FAIXA de valor do perfil, e
 *      antes sobrevivia a essas trocas, seguindo para o rateio já errada.
 *   9. **Trocar o ano zera o modelo** (como em `/carro`): o modelo é indexado por
 *      ano na FIPE, e manter o anterior levaria o preço de outro ano ao PDF.
 *
 * ## O que NÃO mudou, de propósito
 *
 * A sequência do envio (rateio **com** `codigo_cota` — ver B5 em `12-bugs.md` —,
 * depois a cota de participação, depois o lead), o formato de `valorFipe`
 * (`"80668.00"`, que a etapa 2 manda ao PDF), a lista de tipos restrita aos que
 * têm plano de cotação (at-43) e o modal de marcas **sem** motocicleta elétrica
 * (a cotação de bike é a frente C do item 50, `plan-c-bike-cotacao-vistoria.md`).
 */

/** Estado do formulário. Tudo local — o slice é escrito no envio. */
interface EstadoForm {
  nome: string;
  cpf: string;
  email: string;
  /** DDD e número no mesmo campo, como nas telas da adesão (at-58). */
  celular: string;
  placa: string;
  ano: string;
  marca: string;
  modelo: string;
  codigoFipe: string;
  /** Numérico em string (`"80668.00"`) — é o formato que a etapa 2 manda ao PDF. */
  valorFipe: string;
  referenciaFipe: string;
  /** at-84: valor da tabela FIPE 0km em vez do usado do ano. */
  zeroKm: boolean;
  tipoVeiculo: string;
  tipoVeiculoDescricao: string;
  regionalId: string;
  regionalNome: string;
  cooperativaId: string;
  cooperativaNome: string;
  cotaId: string;
  cotaDescricao: string;
  cotaPorcentagemFipe: number | null;
  valorCota: string;
}

type CampoForm = keyof EstadoForm;
type Erros = Partial<Record<CampoForm, string>>;

const VAZIO: EstadoForm = {
  nome: '',
  cpf: '',
  email: '',
  celular: '',
  placa: '',
  ano: '',
  marca: '',
  modelo: '',
  codigoFipe: '',
  valorFipe: '',
  referenciaFipe: '',
  zeroKm: false,
  tipoVeiculo: '',
  tipoVeiculoDescricao: '',
  regionalId: '',
  regionalNome: '',
  cooperativaId: '',
  cooperativaNome: '',
  cotaId: '',
  cotaDescricao: '',
  cotaPorcentagemFipe: null,
  valorCota: '',
};

/**
 * Celular como o backend exige — `(XX) XXXXX-XXXX`, **11 dígitos**.
 *
 * ⚠️ Não é `validarTelefone` de `utils/validacaoAssociado`, que aceita 10 ou 11:
 * `CreateCotacaoDto.celular` tem `@Matches(/^\(\d{2}\) \d{5}-\d{4}$/)`, ou seja,
 * um fixo de 10 dígitos é **recusado com 400** — e era assim que a tela antiga
 * deixava passar. Aqui o campo é de celular e o erro aparece na hora.
 */
function validarCelularDaCotacao(valor: string): string | null {
  const digitos = (valor || '').replace(/\D/g, '');
  if (!digitos) return 'Obrigatório';

  return digitos.length === 11 ? null : 'Informe DDD + celular com 9 dígitos';
}

const CotacaoPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar(HOME());

  /*
   * O lead já criado neste fluxo (at-62, P2). Lido do slice — é o que sobrevive
   * ao "Voltar" da etapa 2 e faz o reenvio virar edição.
   */
  const leadId = useSelector((state: RootState) => state.cotacao.leadId);
  const leadCpf = useSelector((state: RootState) => state.cotacao.leadCpf);

  /*
   * ⚠️ Lido **uma vez**, no primeiro render — mesma decisão do
   * `FormularioAssociado` e do `FormularioVeiculo`: um `useEffect` que
   * sincronizasse apagaria o que o consultor está digitando (o slice muda quando
   * os modais de regional e cooperativa despacham o código escolhido).
   *
   * Numa cotação nova o slice já foi limpo por `clearCotacaoData` — pelo menu ou
   * pelo "Nova cotação" da carteira —, então isto resulta em formulário vazio.
   */
  const [form, setForm] = useState<EstadoForm>(() => {
    const cotacao = store.getState().cotacao;
    if (!cotacao?.cpf && !cotacao?.nome) return VAZIO;

    return {
      ...VAZIO,
      nome: cotacao.nome ?? '',
      cpf: cotacao.cpf ?? '',
      email: cotacao.email ?? '',
      celular: cotacao.celular ?? '',
      placa: cotacao.placa ?? '',
      ano: cotacao.ano ?? '',
      marca: cotacao.marca ?? '',
      modelo: cotacao.modelo ?? '',
      codigoFipe: cotacao.codigoFipe ?? '',
      valorFipe: cotacao.valorFipe ?? '',
      referenciaFipe: cotacao.referenciaFipe ?? '',
      zeroKm: cotacao.zeroKm ?? false,
      tipoVeiculo: cotacao.tipoVeiculo ?? '',
      tipoVeiculoDescricao: cotacao.tipoVeiculoDescricao ?? '',
      regionalId: cotacao.codigoRegionalSelecionada ?? '',
      regionalNome: cotacao.regionalNome ?? '',
      cooperativaId: cotacao.codigoCooperativaSelecionada ?? '',
      cooperativaNome: cotacao.cooperativaNome ?? '',
      cotaId: cotacao.cotaId ?? '',
      cotaDescricao: cotacao.cotaDescricao ?? '',
      cotaPorcentagemFipe: cotacao.cotaPorcentagemFipe ?? null,
      valorCota: cotacao.valorCota ?? '',
    };
  });

  const [erros, setErros] = useState<Erros>({});
  const [salvando, setSalvando] = useState(false);
  const [modalAberto, setModalAberto] = useState<
    null | 'marcas' | 'veiculos' | 'tipos' | 'regionais' | 'cooperativas' | 'cotas'
  >(null);
  const formRef = useRef<HTMLFormElement>(null);

  const fechar = () => setModalAberto(null);

  /** Escreve campos e **limpa os erros deles** — a mensagem não fica de teimosa. */
  const aplicar = (parcial: Partial<EstadoForm>) => {
    setForm((antes) => ({ ...antes, ...parcial }));
    setErros((antes) => {
      const chaves = Object.keys(parcial) as CampoForm[];
      if (!chaves.some((k) => antes[k])) return antes;

      const proximo = { ...antes };
      chaves.forEach((k) => delete proximo[k]);
      return proximo;
    });
  };

  /** A cota depende de perfil e valor: trocar qualquer um deles a invalida. */
  const limparCota = { cotaId: '', cotaDescricao: '', cotaPorcentagemFipe: null, valorCota: '' };

  /**
   * Mostra os erros e **rola até o primeiro**.
   *
   * ⚠️ `scrollIntoView` é chamado defensivamente: não existe no jsdom nem é
   * garantido em toda WebView antiga, e uma exceção aqui abortaria o resto do
   * submit — deixando o formulário sem reação visível ao clique.
   */
  const mostrarErros = (encontrados: Erros) => {
    setErros(encontrados);

    const primeiro = Object.keys(encontrados)[0];
    const alvo = formRef.current?.querySelector(`[data-field="${primeiro}"]`);
    if (typeof (alvo as HTMLElement | null)?.scrollIntoView === 'function') {
      (alvo as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // ── Abertura dos modais com pré-requisito ──────────────────────────────
  //
  // 🔴 Pré-requisito que falta vira ERRO NO CAMPO que falta, não toast e não
  // botão desabilitado com `title`. Toast desaparece; `title` não existe no
  // celular (foi por isso que a at-54 tirou a situação do SGA de um `title`).
  // A dependência também é anunciada ANTES, na `dica` do campo.

  const abrirVeiculos = () => {
    const faltando: Erros = {};
    if (!form.marca) faltando.marca = 'Escolha a marca antes de buscar o modelo';
    if (!form.ano) faltando.ano = 'Escolha o ano antes de buscar o modelo';

    if (Object.keys(faltando).length > 0) {
      mostrarErros(faltando);
      return;
    }

    setModalAberto('veiculos');
  };

  const abrirCotas = () => {
    const faltando: Erros = {};
    if (!form.tipoVeiculo) faltando.tipoVeiculo = 'Escolha o tipo antes da cota';
    if (!form.regionalId) faltando.regionalId = 'Escolha a regional antes da cota';
    if (!form.cooperativaId) faltando.cooperativaId = 'Escolha a cooperativa antes da cota';
    // A cota é escolhida pela faixa de valor — e o valor vem do modelo.
    if (!form.valorFipe) faltando.modelo = 'Escolha o modelo antes da cota';

    if (Object.keys(faltando).length > 0) {
      mostrarErros(faltando);
      return;
    }

    setModalAberto('cotas');
  };

  // ── Validação e envio ──────────────────────────────────────────────────

  const validar = (): Erros => {
    const e: Erros = {};

    e.nome = validarObrigatorio(form.nome) ?? undefined;
    // P1: obrigatório aqui porque a etapa 2 o exige (ver o cabeçalho).
    e.cpf = validarCpfCnpj(form.cpf) ?? undefined;
    e.email = validarEmail(form.email) ?? undefined;
    e.celular = validarCelularDaCotacao(form.celular) ?? undefined;

    if (!form.placa.trim()) e.placa = 'Obrigatório';
    else if (form.placa.length < 7) e.placa = 'Placa incompleta';

    e.ano = form.ano ? undefined : 'Selecione o ano';
    e.marca = form.marca ? undefined : 'Selecione a marca';

    if (!form.modelo) e.modelo = 'Selecione o modelo';
    // Defensivo: cotação retomada de um estado antigo pode ter modelo sem FIPE,
    // e sem ela o rateio receberia `NaN`.
    else if (!form.valorFipe) e.modelo = 'Selecione o modelo de novo para carregar o valor FIPE';

    e.tipoVeiculo = form.tipoVeiculo ? undefined : 'Selecione o tipo de veículo';
    e.regionalId = form.regionalId ? undefined : 'Selecione a regional';
    e.cooperativaId = form.cooperativaId ? undefined : 'Selecione a cooperativa';
    e.cotaId = form.cotaId ? undefined : 'Selecione a cota';

    (Object.keys(e) as CampoForm[]).forEach((k) => {
      if (!e[k]) delete e[k];
    });

    return e;
  };

  /**
   * ⚠️ O evento é **opcional**: além do `submit`, quem chama isto é a bolinha
   * "Produtos" do `Stepper` (at-62). Ir para a frente daqui tem de **gravar** —
   * navegar direto descartaria o que está digitado, porque o formulário é estado
   * local e o slice só é escrito no envio.
   */
  const enviar = async (evento?: React.FormEvent) => {
    evento?.preventDefault();

    const encontrados = validar();
    if (Object.keys(encontrados).length > 0) {
      mostrarErros(encontrados);
      return;
    }

    setSalvando(true);

    try {
      const valorFipeNumero = parseFloat(form.valorFipe);

      /*
       * ⚠️ O rateio vai **com `codigo_cota`** — a cota que o consultor escolheu.
       * A tela de termo o omite, e o resultado pode divergir: é o bug B5 de
       * `12-bugs.md`, mantido como está por decisão de Victor (at-21), porque
       * alinhar mudaria o valor de termos já em uso.
       */
      const rateio = await calcularRateio(
        form.regionalId,
        form.tipoVeiculo,
        valorFipeNumero,
        form.cotaId,
      );

      let participacao = { valor_cota_participacao: 0, porcentagem_fipe: 0 };
      if (form.cotaPorcentagemFipe != null) {
        participacao = await calcularCotaParticipacao({
          porcentagem_fipe: form.cotaPorcentagemFipe,
          valor_fipe: valorFipeNumero,
        });
      }

      const digitosCpf = form.cpf.replace(/\D/g, '');
      const dadosDoLead = {
        nome: form.nome.trim(),
        email: form.email.trim(),
        celular: form.celular,
        cpf: form.cpf,
        placa: form.placa.toUpperCase(),
        marca: form.marca,
        modelo: form.modelo,
        ano: parseInt(form.ano, 10),
      };

      /*
       * 🔴 P2 — EDITAR em vez de criar outro lead.
       *
       * O limite do backend é de **4 cotações por CPF/mês**, contadas por
       * `AssociadoCotacao`. Com o "Voltar" que esta branch acrescentou, reenviar
       * virou gesto natural: sem isto, corrigir um e-mail gastaria duas das
       * quatro, em silêncio.
       *
       * ⚠️ Só edita enquanto o **CPF for o mesmo**. Mudou o CPF, é outra pessoa —
       * repropor o lead antigo o transformaria no novo e faria a cotação anterior
       * sumir. E como o limite é por CPF, criar um lead para o CPF novo não
       * consome nada do que já existe.
       */
      const podeEditar = Boolean(leadId) && leadCpf === digitosCpf;
      const lead = podeEditar
        ? await editarCotacao({ id: leadId, ...dadosDoLead })
        : await criarCotacao(dadosDoLead);

      dispatch(
        setCotacaoData({
          nome: form.nome.trim(),
          email: form.email.trim(),
          celular: form.celular,
          cpf: form.cpf,
          placa: form.placa.toUpperCase(),
          marca: form.marca,
          modelo: form.modelo,
          ano: form.ano,
          codigoFipe: form.codigoFipe,
          valorFipe: form.valorFipe,
          referenciaFipe: form.referenciaFipe,
          zeroKm: form.zeroKm,
          tipoVeiculo: form.tipoVeiculo,
          tipoVeiculoDescricao: form.tipoVeiculoDescricao,
          codigoRegionalSelecionada: form.regionalId,
          regionalNome: form.regionalNome,
          codigoCooperativaSelecionada: form.cooperativaId,
          cooperativaNome: form.cooperativaNome,
          cotaId: form.cotaId,
          cotaDescricao: form.cotaDescricao,
          cotaPorcentagemFipe: form.cotaPorcentagemFipe,
          valorCota: form.valorCota,
          valorRateio: rateio.valorRateio || 0,
          valorCotaParticipacao: participacao.valor_cota_participacao,
          porcentagemFipe: participacao.porcentagem_fipe,
          leadId: podeEditar ? leadId : (lead?.id ?? ''),
          leadCpf: digitosCpf,
        }),
      );

      // ⚠️ Sem `setTimeout`: o toast sobrevive à navegação (container único, at-19).
      navigate(COTACAO_PRODUTO());
    } catch (erro: any) {
      toast.error(erro?.message || 'Erro ao salvar a cotação');
      setSalvando(false);
    }
  };

  const anos = anosDisponiveis();
  const valorFipeExibicao = form.valorFipe ? numeroParaMoeda(parseMoeda(form.valorFipe)) : '';

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Cotação" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              {/*
                As bolinhas navegam (at-62). Daqui só há um destino — a etapa 2 —
                e ir para lá **grava**: é o mesmo caminho do botão "Continuar",
                validação inclusive. Se faltar algo, os erros aparecem nos campos,
                que é também a explicação de por que a etapa ainda não fechou.
              */}
              <Stepper
                etapa={1}
                rotulos={ETAPAS_COTACAO}
                aoIrPara={(destino) => {
                  if (destino === 2 && !salvando) void enviar();
                }}
              />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <form
                ref={formRef}
                onSubmit={enviar}
                noValidate
                className="tw-flex tw-flex-col tw-gap-8"
              >
                {/* ── 1. Cliente ──────────────────────────────────────── */}
                <FormSection titulo="Dados do Cliente" icone="pessoa">
                  <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                    <Field
                      rotulo="Nome completo"
                      obrigatorio
                      erro={erros.nome}
                      campo="nome"
                      className="sm:tw-col-span-2"
                    >
                      <Input
                        type="text"
                        autoComplete="name"
                        placeholder="Como está no documento"
                        maxLength={100}
                        value={form.nome}
                        onChange={(evento) => aplicar({ nome: evento.target.value })}
                      />
                    </Field>

                    <Field
                      rotulo="CPF / CNPJ"
                      obrigatorio
                      erro={erros.cpf}
                      campo="cpf"
                      dica="Necessário para gerar o PDF da cotação."
                    >
                      <Input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="000.000.000-00"
                        maxLength={18}
                        value={form.cpf}
                        onChange={(evento) => aplicar({ cpf: formatCpfCnpj(evento.target.value) })}
                      />
                    </Field>

                    <Field rotulo="Celular" obrigatorio erro={erros.celular} campo="celular">
                      <Input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="(11) 99999-9999"
                        maxLength={16}
                        value={form.celular}
                        onChange={(evento) =>
                          aplicar({ celular: formatTelefone(evento.target.value) })
                        }
                      />
                    </Field>

                    <Field
                      rotulo="E-mail"
                      obrigatorio
                      erro={erros.email}
                      campo="email"
                      className="sm:tw-col-span-2"
                    >
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="exemplo@email.com.br"
                        value={form.email}
                        onChange={(evento) => aplicar({ email: evento.target.value })}
                      />
                    </Field>
                  </div>
                </FormSection>

                {/* ── 2. Veículo ──────────────────────────────────────── */}
                <FormSection titulo="Veículo" icone="veiculo">
                  <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                    <Field
                      rotulo="Placa"
                      obrigatorio
                      erro={erros.placa}
                      campo="placa"
                      dica="Formato antigo (AAA1234) ou Mercosul (AAA1A23)."
                    >
                      <Input
                        type="text"
                        autoComplete="off"
                        placeholder="ABC1D23"
                        value={form.placa}
                        onChange={(evento) => {
                          const limpa = formatPlaca(evento.target.value);
                          // Placa completa fora dos dois formatos: a tecla é
                          // recusada, como em `/carro` (at-59).
                          if (limpa.length === 7 && !placaValida(limpa)) return;
                          aplicar({ placa: limpa });
                        }}
                      />
                    </Field>

                    {/* at-84: o modo 0km troca o PREÇO (tabela FIPE 0km × usado do
                        ano), então limpa o modelo e a cota, como trocar o ano. */}
                    <Checkbox
                      className="sm:tw-col-span-2"
                      rotulo="Veículo 0km"
                      descricao="Usa o preço da tabela FIPE 0km, e não o do veículo usado do ano escolhido."
                      checked={form.zeroKm}
                      onChange={(marcado) =>
                        aplicar({
                          zeroKm: marcado,
                          modelo: '',
                          codigoFipe: '',
                          valorFipe: '',
                          referenciaFipe: '',
                          ...limparCota,
                        })
                      }
                    />

                    <Field rotulo="Ano do Modelo" obrigatorio erro={erros.ano} campo="ano">
                      <Select
                        opcoes={anos}
                        placeholder="Selecione o ano"
                        value={form.ano}
                        onChange={(evento) =>
                          /* O modelo é indexado por ano na FIPE: trocar o ano
                             invalida o que foi escolhido (mesma regra de `/carro`). */
                          aplicar({
                            ano: evento.target.value,
                            modelo: '',
                            codigoFipe: '',
                            valorFipe: '',
                            referenciaFipe: '',
                            ...limparCota,
                          })
                        }
                      />
                    </Field>

                    <Field rotulo="Marca" obrigatorio erro={erros.marca} campo="marca">
                      <BotaoSelecao
                        valor={form.marca}
                        vazio="Nenhuma marca selecionada"
                        temErro={!!erros.marca}
                        aoAbrir={() => setModalAberto('marcas')}
                      />
                    </Field>

                    <Field
                      rotulo="Modelo"
                      obrigatorio
                      erro={erros.modelo}
                      campo="modelo"
                      dica={
                        !form.marca || !form.ano ? 'Escolha a marca e o ano primeiro.' : undefined
                      }
                    >
                      <BotaoSelecao
                        valor={form.modelo}
                        vazio="Nenhum modelo selecionado"
                        temErro={!!erros.modelo}
                        aoAbrir={abrirVeiculos}
                      />
                    </Field>

                    {/* at-21: o valor FIPE do modelo escolhido é fator de decisão
                        do consultor, e o mês de referência importa porque o
                        espelho pode estar defasado (item 14). */}
                    <Field
                      rotulo="Valor FIPE"
                      campo="valorFipe"
                      className="sm:tw-col-span-2"
                      dica={
                        form.referenciaFipe
                          ? `${form.zeroKm ? 'FIPE 0km · referência' : 'Referência FIPE'}: ${form.referenciaFipe}`
                          : undefined
                      }
                    >
                      <Input
                        type="text"
                        value={valorFipeExibicao}
                        readOnly
                        placeholder="Carregado ao selecionar o modelo"
                      />
                    </Field>
                  </div>
                </FormSection>

                {/* ── 3. Perfil ───────────────────────────────────────── */}
                <FormSection titulo="Perfil da Cotação" icone="cotacoes">
                  <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                    <Field
                      rotulo="Tipo de Veículo"
                      obrigatorio
                      erro={erros.tipoVeiculo}
                      campo="tipoVeiculo"
                      dica="Define o plano impresso no PDF."
                    >
                      <BotaoSelecao
                        valor={form.tipoVeiculoDescricao}
                        vazio="Nenhum tipo selecionado"
                        temErro={!!erros.tipoVeiculo}
                        aoAbrir={() => setModalAberto('tipos')}
                      />
                    </Field>

                    <Field
                      rotulo="Regional"
                      obrigatorio
                      erro={erros.regionalId}
                      campo="regionalId"
                    >
                      <BotaoSelecao
                        valor={form.regionalNome}
                        vazio="Nenhuma regional selecionada"
                        temErro={!!erros.regionalId}
                        aoAbrir={() => setModalAberto('regionais')}
                      />
                    </Field>

                    <Field
                      rotulo="Cooperativa"
                      obrigatorio
                      erro={erros.cooperativaId}
                      campo="cooperativaId"
                    >
                      <BotaoSelecao
                        valor={form.cooperativaNome}
                        vazio="Nenhuma cooperativa selecionada"
                        temErro={!!erros.cooperativaId}
                        aoAbrir={() => setModalAberto('cooperativas')}
                      />
                    </Field>

                    <Field
                      rotulo="Cota"
                      obrigatorio
                      erro={erros.cotaId}
                      campo="cotaId"
                      dica="Depende do modelo, do tipo, da regional e da cooperativa."
                    >
                      <BotaoSelecao
                        valor={form.cotaDescricao}
                        vazio="Nenhuma cota selecionada"
                        temErro={!!erros.cotaId}
                        aoAbrir={abrirCotas}
                      />
                    </Field>
                  </div>
                </FormSection>

                <FormActions>
                  <Botao
                    type="submit"
                    tamanho="lg"
                    carregando={salvando}
                    iconeDireita={salvando ? undefined : 'chevron-direita'}
                    larguraTotal
                    className="sm:tw-w-auto sm:tw-min-w-[220px]"
                  >
                    {salvando ? 'Salvando...' : 'Continuar — Produtos'}
                  </Botao>
                </FormActions>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/*
        ⚠️ **Sem `incluirBikes`**: a cotação de motocicleta elétrica é a frente C
        do item 50 (`plan-c-bike-cotacao-vistoria.md`) — o backend ainda exige
        `codigoFipe`/`valorFipe` no PDF e o tipo 35 não tem plano de cotação. Há
        teste sentinela em `ModalMarcas.test.tsx` sobre este caso.
      */}
      <ModalMarcas
        isOpen={modalAberto === 'marcas'}
        onClose={fechar}
        onSelectMarca={(marca) => {
          aplicar({
            marca: marca.descricao,
            modelo: '',
            codigoFipe: '',
            valorFipe: '',
            referenciaFipe: '',
            ...limparCota,
          });
          fechar();
        }}
      />

      <ModalVeiculos
        isOpen={modalAberto === 'veiculos'}
        onClose={fechar}
        year={parseInt(form.ano, 10)}
        brand={form.marca}
        zeroKm={form.zeroKm}
        onSelectVeiculo={(veiculo) => {
          aplicar({
            modelo: veiculo.model,
            /*
             * ⚠️ at-84: no modo 0km o registro FIPE tem ano 32000 (é assim que a base
             * grava o preço 0km). O ano da cotação continua sendo o que o consultor
             * escolheu — ele vai no lead e no PDF.
             */
            ano: form.zeroKm ? form.ano : String(veiculo.modelYear),
            codigoFipe: veiculo.codeFipe,
            /*
             * ⚠️ Transformação preservada verbatim da tela antiga: "R$ 80.668,00"
             * vira **"80668.00"**. É esse formato que a etapa 2 manda no
             * `valorFipe` do PDF e que o backend lê para os compulsórios —
             * mudá-lo alteraria o documento.
             */
            valorFipe: veiculo.price.replace(/[^\d,]/g, '').replace(',', '.'),
            referenciaFipe: veiculo.referenceMonth || '',
            ...limparCota,
          });
          fechar();
        }}
      />

      {/* at-43: só os tipos que TÊM plano de cotação ativo — o tipo escolhido
          aqui vira o `codigoPlano` do PDF, e sem plano a geração dá 400. */}
      <ModalTipoVeiculos
        isOpen={modalAberto === 'tipos'}
        onClose={fechar}
        somenteComPlano
        onSelectTipoVeiculo={(tipo: TipoVeiculo) => {
          aplicar({
            tipoVeiculo: tipo.codigo_tipo,
            tipoVeiculoDescricao: tipo.descricao_tipo,
            ...limparCota,
          });
          fechar();
        }}
      />

      <ModalRegionaisUsuario
        isOpen={modalAberto === 'regionais'}
        onClose={fechar}
        onSelectRegional={(regional) => {
          aplicar({
            regionalId: regional.codigo_regional,
            regionalNome: regional.nome,
            ...limparCota,
          });
          fechar();
        }}
      />

      <ModalCooperativasUsuario
        isOpen={modalAberto === 'cooperativas'}
        onClose={fechar}
        onSelectCooperativa={(cooperativa) => {
          aplicar({
            cooperativaId: cooperativa.codigo_cooperativa,
            cooperativaNome: cooperativa.nome,
            ...limparCota,
          });
          fechar();
        }}
      />

      <ModalCotas
        isOpen={modalAberto === 'cotas'}
        onClose={fechar}
        codigoRegional={form.regionalId}
        codigoCooperativa={form.cooperativaId}
        codigoTipoVeiculo={form.tipoVeiculo}
        valorFipe={form.valorFipe}
        onSelectCota={(cota: Cota) => {
          aplicar({
            cotaId: cota.codigo_cota,
            cotaDescricao: cota.descricao_cota,
            cotaPorcentagemFipe: cota.porcentagem_fipe,
            valorCota: cota.valor_cota || '',
          });
          fechar();
        }}
      />
    </ShellSaac>
  );
};

export default CotacaoPage;
